/**
 * OpenAI calls go through server.js so the API key stays on the server only.
 * Dev: use package.json "proxy" → http://localhost:3003, or set REACT_APP_API_URL.
 * Production: set REACT_APP_API_URL to your deployed API origin.
 */

export type OpenAIChatMessage = {
  role: 'system' | 'user' | 'assistant';
  content: string;
};

function apiUrl(path: string): string {
  const base = (process.env.REACT_APP_API_URL || '').trim().replace(/\/$/, '');
  return base ? `${base}${path}` : path;
}

export type OpenAIResponseFormat = { type: 'json_object' } | { type: 'text' };

export async function fetchOpenAIChatCompletion(params: {
  model?: string;
  messages: OpenAIChatMessage[];
  max_tokens?: number;
  temperature?: number;
  response_format?: OpenAIResponseFormat;
}): Promise<string> {
  const url = apiUrl('/api/openai/chat');
  const isDev = process.env.NODE_ENV === 'development';

  let res: Response;
  try {
    res = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        model: params.model ?? 'gpt-3.5-turbo',
        messages: params.messages,
        max_tokens: params.max_tokens,
        temperature: params.temperature,
        response_format: params.response_format
      })
    });
  } catch {
    throw new Error(
      isDev
        ? `Could not reach ${url}. Run "npm run dev" OR "npm run server" (port 3003) while using "npm start". The chatbot does not read .env by itself — only server.js does.`
        : 'Could not reach the AI service.'
    );
  }

  const raw = await res.text();
  let data: { content?: string; error?: string };
  try {
    data = JSON.parse(raw) as { content?: string; error?: string };
  } catch {
    throw new Error(
      isDev
        ? `Bad response (${res.status}) from ${url} — not JSON. Often means no API server (use npm run dev) or you're on a static deploy without /api.`
        : `OpenAI proxy failed (${res.status})`
    );
  }

  if (!res.ok) {
    throw new Error(
      data.error ||
        (isDev
          ? `Proxy returned ${res.status}. If 503: server has no OPENAI_API_KEY — restart terminal after editing .env.`
          : `Request failed (${res.status})`)
    );
  }

  if (typeof data.content !== 'string') {
    throw new Error(isDev ? 'Proxy OK but missing content — check terminal running server.js' : 'Invalid proxy response');
  }

  return data.content;
}
